import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';

function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setChecking(false);
      if (!data.session) {
        navigate('/auth/forgot-password', { replace: true });
      }
    });
    return () => {
      mounted = false;
    };
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setStatus('error');
      setErrorMsg('Les mots de passe ne correspondent pas.');
      return;
    }
    if (password.length < 8) {
      setStatus('error');
      setErrorMsg('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
      return;
    }

    await supabase.auth.signOut();
    navigate('/auth/login', { replace: true, state: { reset: true } });
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-500/10">
            <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
          </div>
          <p className="text-sm text-foreground-400">Vérification de votre session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-4 py-12 md:py-20">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img
              src="https://static.readdy.ai/image/4348d35aef10b02e64c1a80f787cca17/52f74c8d2b3c4f047b22c4e6b94b8b4b.jpeg"
              alt="WorldApeiron"
              className="h-14 w-14 mx-auto mb-4 object-contain"
            />
            <h1 className="font-heading font-bold text-2xl text-foreground-50">
              Nouveau mot de passe
            </h1>
            <p className="text-sm text-foreground-400 mt-2">
              Choisissez un nouveau mot de passe sécurisé
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground-400 mb-1.5">
                Nouveau mot de passe
              </label>
              <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                <div className="w-10 h-10 flex items-center justify-center shrink-0">
                  <i className="ri-lock-line text-foreground-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pr-3 outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="w-10 h-10 flex items-center justify-center shrink-0 text-foreground-400 hover:text-foreground-400"
                >
                  <i className={showPassword ? 'ri-eye-line' : 'ri-eye-off-line'} />
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground-400 mb-1.5">
                Confirmer le mot de passe
              </label>
              <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                <div className="w-10 h-10 flex items-center justify-center shrink-0">
                  <i className="ri-lock-line text-foreground-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pr-3 outline-none"
                />
              </div>
            </div>

            {status === 'error' && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">
                {errorMsg}
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {status === 'loading' ? 'Mise à jour...' : 'Réinitialiser le mot de passe'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-foreground-400">
            <Link to="/auth/login" className="text-primary-400 hover:text-primary-300 font-medium transition-colors">
              Retour à la connexion
            </Link>
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ResetPassword;